// Give this person a coupon, or a one-off courtesy.
//
// A courtesy with no coupon behind it is the commoner case — goodwill after
// something went wrong — and should not require inventing a code for it. Either
// way it lands as a credit against their number, comes off their next booking
// automatically, and is only spent once that payment is captured.
import React, { useCallback, useEffect, useState } from 'react';
import {
  Accordion, AccordionSummary, AccordionDetails, Typography, Box, TextField,
  Button, MenuItem, Alert, Chip, Table, TableBody, TableCell, TableRow,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { supabase } from './supabaseClient';

const usd = (cents) => `$${(Number(cents || 0) / 100).toFixed(2)}`;

export default function ProfileCourtesy({ phoneNumber, userType }) {
  const [coupons, setCoupons] = useState([]);
  const [credits, setCredits] = useState([]);
  const [message, setMessage] = useState(null);
  const [saving, setSaving] = useState(false);

  const [source, setSource] = useState('courtesy'); // 'courtesy' | a coupon code
  const [kind, setKind] = useState('amount');
  const [value, setValue] = useState('');
  const [note, setNote] = useState('');

  const [money, setMoney] = useState('');
  const [moneyNote, setMoneyNote] = useState('');
  const [balanceCents, setBalanceCents] = useState(null);
  const isStylist = String(userType || '').toLowerCase() === 'stylist';

  // Money means different things for the two. A stylist is owed it, so it goes
  // to their earnings and joins whatever they cash out next. A client spends
  // it, so it is a balance the next booking draws down.
  const addMoney = async () => {
    const dollars = Number(money);
    if (!Number.isFinite(dollars) || dollars === 0 || !moneyNote.trim()) {
      setMessage({ severity: 'warning', text: 'An amount and a reason, please.' });
      return;
    }
    setSaving(true);
    setMessage(null);
    const { data, error } = await supabase.rpc('admin_credit_account', {
      p_phone_number: phoneNumber,
      p_amount_cents: Math.round(dollars * 100),
      p_reason: moneyNote.trim(),
    });
    if (error) {
      setMessage({ severity: 'error', text: error.message });
    } else {
      setMoney(''); setMoneyNote('');
      setMessage({
        severity: 'success',
        text: data?.applied_to === 'earnings'
          ? 'Added to their earnings. It joins their next cash out.'
          : 'Added to their balance. It comes off their next booking.',
      });
      await load();
    }
    setSaving(false);
  };

  const load = useCallback(async () => {
    if (!phoneNumber) return;
    const [{ data: c }, { data: g }] = await Promise.all([
      supabase.from('coupons').select('*').eq('is_active', true).order('code'),
      supabase.from('profile_credits').select('*').eq('phone_number', phoneNumber)
        .order('granted_at', { ascending: false }),
    ]);
    setCoupons(c || []);
    setCredits(g || []);
    const { data: bal } = await supabase.rpc('account_balance_cents', { p_phone_number: phoneNumber });
    setBalanceCents(bal == null ? null : Number(bal));
  }, [phoneNumber]);

  useEffect(() => { load(); }, [load]);

  const give = async () => {
    const coupon = source === 'courtesy' ? null : coupons.find((c) => c.code === source);
    const numeric = Number(value);
    const effectiveKind = coupon ? coupon.kind : kind;
    const effectiveValue = coupon
      ? (coupon.kind === 'percent' ? coupon.value_percent : coupon.amount_cents)
      : (kind === 'percent' ? Math.round(numeric) : Math.round(numeric * 100));

    if (!note.trim()) {
      setMessage({ severity: 'warning', text: 'Say why — it goes on the record.' });
      return;
    }
    if (!coupon && (!Number.isFinite(numeric) || numeric <= 0)) {
      setMessage({ severity: 'warning', text: 'How much?' });
      return;
    }

    setSaving(true);
    setMessage(null);
    const { error } = await supabase.rpc('admin_grant_profile_credit', {
      p_phone_number: phoneNumber,
      p_kind: effectiveKind,
      p_value: effectiveValue,
      p_note: note.trim(),
      p_coupon_code: coupon ? coupon.code : null,
      p_expires_at: null,
    });
    if (error) {
      setMessage({ severity: 'error', text: error.message });
    } else {
      setValue(''); setNote('');
      setMessage({ severity: 'success', text: 'Given. It comes off their next booking.' });
      await load();
    }
    setSaving(false);
  };

  const describe = (c) => (c.kind === 'percent' ? `${c.value_percent}% off` : `${usd(c.amount_cents)} off`);
  const state = (c) => {
    if (c.consumed_at) return <Chip size="small" label="Used" />;
    if (c.reserved_booking_id) return <Chip size="small" color="warning" label="Held for a booking" />;
    if (c.expires_at && new Date(c.expires_at) < new Date()) return <Chip size="small" label="Expired" />;
    return <Chip size="small" color="success" label="Waiting" />;
  };

  const waiting = credits.filter((c) => !c.consumed_at);

  return (
    <Accordion sx={{ mb: 1, borderRadius: 2, '&:before': { display: 'none' } }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Coupons &amp; courtesies
          {waiting.length > 0 && (
            <Chip size="small" color="success" label={`${waiting.length} waiting`} sx={{ ml: 1 }} />
          )}
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        {message && <Alert severity={message.severity} sx={{ mb: 2 }}>{message.text}</Alert>}

        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
          {isStylist ? 'Add money to their earnings' : 'Add money to their balance'}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>
          {isStylist
            ? 'Goes straight to their available earnings and joins whatever they cash out next.'
            : `Spent automatically on their next booking.${
                balanceCents != null ? ` They currently have ${usd(balanceCents)}.` : ''}`}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <TextField size="small" label="Amount ($)" value={money} onChange={(e) => setMoney(e.target.value)} sx={{ width: 140 }} />
          <TextField size="small" label="Reason" value={moneyNote} onChange={(e) => setMoneyNote(e.target.value)} fullWidth />
          <Button variant="contained" onClick={addMoney} disabled={saving}>Add</Button>
        </Box>

        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>Give a coupon or courtesy</Typography>

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
          <TextField size="small" select label="Give" value={source} onChange={(e) => setSource(e.target.value)} sx={{ width: 200 }}>
            <MenuItem value="courtesy">A one-off courtesy</MenuItem>
            {coupons.map((c) => (
              <MenuItem key={c.id} value={c.code}>{c.code} — {describe(c)}</MenuItem>
            ))}
          </TextField>

          {source === 'courtesy' && (
            <>
              <TextField size="small" select label="Type" value={kind} onChange={(e) => setKind(e.target.value)} sx={{ width: 140 }}>
                <MenuItem value="amount">Fixed amount</MenuItem>
                <MenuItem value="percent">Percentage</MenuItem>
              </TextField>
              <TextField
                size="small"
                label={kind === 'percent' ? 'Percent off' : 'Amount off ($)'}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                sx={{ width: 150 }}
              />
            </>
          )}
        </Box>

        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <TextField size="small" label="Reason" value={note} onChange={(e) => setNote(e.target.value)} fullWidth />
          <Button variant="contained" onClick={give} disabled={saving}>Give</Button>
        </Box>

        {credits.length === 0 ? (
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Nothing given to this person yet.
          </Typography>
        ) : (
          <Table size="small">
            <TableBody>
              {credits.map((c) => (
                <TableRow key={c.id}>
                  <TableCell>{describe(c)}</TableCell>
                  <TableCell>{c.note}</TableCell>
                  <TableCell>{c.granted_by || '—'}</TableCell>
                  <TableCell>{new Date(c.granted_at).toLocaleDateString()}</TableCell>
                  <TableCell align="right">{state(c)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </AccordionDetails>
    </Accordion>
  );
}
